import { useCallback, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useSession } from '../hooks/useSession'
import { usePageTitle } from '../hooks/usePageTitle'
import { LEGACY_PRODUCT_NAME } from '../lib/product'
import { api } from '../lib/api'
import type { CreateSessionBody, SessionCreatedResponse } from '../lib/types'
import { loadSessionSnapshot, saveSessionSnapshot } from '../lib/sessionSnapshot'
import { ResultsList } from '../components/results/ResultsList'
import { GameDetailDrawer } from '../components/results/GameDetailDrawer'
import { recapRosterSize, recapVibeLabel } from '../components/results/humanize'
import { Button, Spinner } from '../components/ui'

export function SessionResultsScreen({ sessionId }: { sessionId: string }) {
  usePageTitle("Tonight's picks", LEGACY_PRODUCT_NAME)
  const { session, error, isLoading } = useSession(sessionId)
  const navigate = useNavigate()
  const [detailAppid, setDetailAppid] = useState<number | null>(null)

  // Recap context: the wizard snapshot when this tab rolled the session, else
  // the session payload itself (deep links must never read "0 players").
  const snapshot = loadSessionSnapshot(sessionId)
  const rosterSize = recapRosterSize(snapshot, session)
  const vibeLabel = recapVibeLabel(snapshot, session)
  const wildcardOptIn = snapshot?.wildcard_opt_in ?? false
  // Without stored inputs, re-POST actions can't recreate the session — the
  // list hides them rather than navigating away unexplained.
  const canRecreate = snapshot != null

  // Re-roll / reveal: re-POST reusing the snapshot context.
  const recreate = useCallback(
    async (overrides: Partial<CreateSessionBody>) => {
      if (!snapshot) return
      try {
        const body: CreateSessionBody = { ...snapshot, ...overrides }
        const resp = await api.post<SessionCreatedResponse>('/sessions', body)
        saveSessionSnapshot(resp.session.id, body)
        navigate({ to: '/steam/sessions/$sessionId', params: { sessionId: resp.session.id } })
      } catch {
        // Stay on the current results if the re-roll fails.
      }
    },
    [snapshot, navigate],
  )

  const handleReroll = useCallback(() => recreate({}), [recreate])
  const handleReveal = useCallback(() => recreate({ wildcard_opt_in: true }), [recreate])
  const handleTweak = useCallback(() => {
    if (snapshot?.group_id) {
      navigate({ to: '/steam/groups/$groupId/play', params: { groupId: snapshot.group_id } })
    } else {
      navigate({ to: '/steam/groups' })
    }
  }, [snapshot, navigate])

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: '/steam/groups' })}
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" />
          Groups
        </Button>
        <h1 className="font-display text-2xl text-text-strong">Tonight&apos;s picks</h1>
        <span className="w-16" aria-hidden="true" />
      </div>

      {isLoading && !session ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : error && !session ? (
        <p className="text-sm text-danger-fg">{error}</p>
      ) : session ? (
        <ResultsList
          session={session}
          rosterSize={rosterSize}
          wildcardOptIn={wildcardOptIn}
          vibeLabel={vibeLabel}
          canRecreate={canRecreate}
          onReroll={handleReroll}
          onRevealWildcards={handleReveal}
          onTweak={handleTweak}
          onOpenDetail={setDetailAppid}
        />
      ) : null}

      <GameDetailDrawer appid={detailAppid} onClose={() => setDetailAppid(null)} />
    </div>
  )
}
